export type ChartTzMode = 'local' | 'market' | 'utc';
import { formatDateTimeInTimezone, isValidIanaTimezone } from '../time/displayTimezone';

type TimeLike = { time: number };

export function normalizeMarkerTimeSec(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 1_000_000_000_000 ? Math.floor(n / 1000) : Math.floor(n);
}

export function normalizeMarkerTimesToSeconds<T extends TimeLike>(markers: T[]): T[] {
  if (!Array.isArray(markers) || markers.length === 0) return [];
  return markers
    .map((marker) => {
      const normalizedTime = normalizeMarkerTimeSec(marker?.time);
      if (!Number.isFinite(normalizedTime)) return null;
      if (normalizedTime === Number(marker.time)) return marker;
      return { ...marker, time: normalizedTime } as T;
    })
    .filter((marker): marker is T => marker !== null);
}

function toFormatter(mode: ChartTzMode, withDate: boolean): Intl.DateTimeFormat {
  const base: Intl.DateTimeFormatOptions = withDate
    ? { month: 'short', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: '2-digit', minute: '2-digit', hour12: false };

  if (mode === 'market') return new Intl.DateTimeFormat('en-US', { ...base, timeZone: 'America/New_York' });
  if (mode === 'utc') return new Intl.DateTimeFormat('en-US', { ...base, timeZone: 'UTC' });
  return new Intl.DateTimeFormat('en-US', base);
}

export function formatChartTime(epochSec: number, mode: ChartTzMode, withDate = false, displayTimezone?: string): string {
  const n = Number(epochSec);
  if (!Number.isFinite(n)) return '';
  const dt = new Date(Math.floor(n) * 1000);

  // Daily bars from Twelve Data use UTC-midnight as their trading-date label.
  // Displaying them with a NY-time hour would show the previous calendar evening.
  // Detect UTC midnight (seconds align to start of day at 00:00:00 UTC with t % 86400 === 0)
  // and render date-only to avoid the off-by-one-day problem.
  if (Math.floor(n) % 86400 === 0 && dt.getUTCHours() === 0 && dt.getUTCMinutes() === 0) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: '2-digit',
      year: '2-digit',
    });
    return dateFormatter.format(dt);
  }

  if (isValidIanaTimezone(displayTimezone)) {
    const formatted = formatDateTimeInTimezone(dt.getTime(), displayTimezone);
    return withDate ? `${formatted.dateStr} ${formatted.timeStr.slice(0, 5)}` : formatted.timeStr.slice(0, 5);
  }

  return toFormatter(mode, withDate).format(dt);
}

export function formatChartTimeCompactDate(epochSec: number, mode: ChartTzMode, displayTimezone?: string): string {
  const n = Number(epochSec);
  if (!Number.isFinite(n)) return '';
  const timeZone = isValidIanaTimezone(displayTimezone) ? displayTimezone : mode === 'market' ? 'America/New_York' : mode === 'utc' ? 'UTC' : undefined;
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date(Math.floor(n) * 1000)).map((part) => [part.type, part.value]),
  );
  return `${parts.month}/${parts.day} — ${parts.hour}:${parts.minute}`;
}

function getNyParts(epochSec: number): { weekday: number; hh: number; mm: number } | null {
  const n = Number(epochSec);
  if (!Number.isFinite(n)) return null;
  const dt = new Date(Math.floor(n) * 1000);

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = fmt.formatToParts(dt);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const weekdayRaw = String(map.weekday || '').toLowerCase();
  const hh = Number(map.hour);
  const mm = Number(map.minute);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;

  const weekday =
    weekdayRaw.startsWith('sun') ? 0 :
    weekdayRaw.startsWith('mon') ? 1 :
    weekdayRaw.startsWith('tue') ? 2 :
    weekdayRaw.startsWith('wed') ? 3 :
    weekdayRaw.startsWith('thu') ? 4 :
    weekdayRaw.startsWith('fri') ? 5 : 6;

  return { weekday, hh, mm };
}

/**
 * Checks if a candle-start timestamp belongs to Regular Trading Hours (RTH).
 * Half-open interval: [09:30, 16:00) NY time (America/New_York).
 * Candle start must satisfy: 09:30 <= candleStart < 16:00 NY time.
 */
export function isRthBar(epochSec: number): boolean {
  const p = getNyParts(epochSec);
  if (!p) return false;
  if (p.weekday === 0 || p.weekday === 6) return false;
  const minutes = p.hh * 60 + p.mm;
  return minutes >= 9 * 60 + 30 && minutes < 16 * 60;
}

/**
 * Checks if a candle-start timestamp belongs to Full Session.
 * Half-open interval: [04:00, 20:00) NY time (America/New_York).
 * Candle start must satisfy: 04:00 <= candleStart < 20:00 NY time.
 */
export function isFullSessionBar(epochSec: number): boolean {
  const p = getNyParts(epochSec);
  if (!p) return false;
  if (p.weekday === 0 || p.weekday === 6) return false;
  const minutes = p.hh * 60 + p.mm;
  return minutes >= 4 * 60 && minutes < 20 * 60;
}

/**
 * Filters intraday OHLC bars to a detected trade session window.
 *
 * - Only STK bars are filtered (Futures/Forex/Crypto use other session models).
 * - 1D (daily) bars use UTC-midnight trading-date labels and are never
 *   filtered by intraday hours.
 * - When `rangeSec` is null (multi-day trades) bars are returned untouched so
 *   users can scroll to every execution.
 * - `keepTimes` holds execution candle timestamps that must never be dropped
 *   even if they fall just outside the session window.
 *
 * @param bars        Array of bars (must have .time in UNIX seconds)
 * @param rangeSec    Detected session window [startSec, endSec) for the trade
 *                    date, or null to skip intraday filtering
 * @param assetClass  Asset class string; only STK is filtered by session
 * @param timeframe   Optional: '1d' skips intraday filtering entirely
 * @param keepTimes   Optional set of candle times that must be preserved
 */
export function filterBarsBySession<T extends TimeLike>(
  bars: T[],
  rangeSec: { startSec: number; endSec: number } | null,
  assetClass: string,
  timeframe?: string,
  keepTimes?: ReadonlySet<number>,
): T[] {
  const secType = String(assetClass || '').toUpperCase();
  if (!Array.isArray(bars)) return [];
  if (secType !== 'STK') return bars;
  // Daily bars use UTC-midnight as their timestamp convention — they represent
  // the trading date, not a wall-clock candle start time. Skip intraday filtering.
  const tf = String(timeframe || '').toLowerCase().replace(/\s/g, '');
  if (tf === '1d' || tf === 'day' || tf === '1day') return bars;
  // Multi-day trades keep the complete requested window.
  if (!rangeSec) return bars;

  const { startSec, endSec } = rangeSec;
  return bars.filter((b) => {
    const t = Number((b as any).time ?? (b as any).t);
    if (!Number.isFinite(t)) return false;
    // Never drop a candle that carries a trade execution.
    if (keepTimes && keepTimes.has(t)) return true;
    return t >= startSec && t < endSec;
  });
}

export function filterMarkersToVisibleTimes<T extends TimeLike>(markers: T[], visibleBars: TimeLike[]): T[] {
  if (!Array.isArray(markers) || markers.length === 0) return [];
  const normalizedMarkers = normalizeMarkerTimesToSeconds(markers);
  const visible = new Set(
    (visibleBars || [])
      .map((b) => normalizeMarkerTimeSec((b as any)?.time))
      .filter((t): t is number => Number.isFinite(t))
  );
  return normalizedMarkers.filter((m) => {
    const markerTimeSec = normalizeMarkerTimeSec(m.time);
    return markerTimeSec != null && visible.has(markerTimeSec);
  });
}

export function filterMarkersForDisplay<T extends TimeLike>(
  markers: T[],
  visibleBars: TimeLike[],
  filterToVisible: boolean,
  didFilterBars: boolean,
): T[] {
  const normalizedMarkers = normalizeMarkerTimesToSeconds(markers);
  if (!(filterToVisible && didFilterBars)) return normalizedMarkers;
  return filterMarkersToVisibleTimes(normalizedMarkers, visibleBars);
}

// Format a timestamp (ms) into separate date/time strings using the provided mode.
export function formatDateTimeForMode(timestampMs: number, mode: ChartTzMode): { dateStr: string; timeStr: string } {
  const n = Number(timestampMs);
  if (!Number.isFinite(n)) return { dateStr: '', timeStr: '' };
  const dt = new Date(Math.floor(n));

  const tz = mode === 'market' ? 'America/New_York' : mode === 'utc' ? 'UTC' : undefined;

  // Date: DD MMM YYYY (e.g., 01 Feb 2026)
  const dateFmt = tz
    ? new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })
    : new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  const parts = dateFmt.formatToParts(dt);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const day = map.day || '';
  const month = map.month || '';
  const year = map.year || '';
  const dateStr = `${day} ${month} ${year}`.trim();

  // Time: HH:mm:ss (24-hour)
  const timeFmt = tz
    ? new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz })
    : new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const timeParts = timeFmt.formatToParts(dt);
  const tmap = Object.fromEntries(timeParts.map((p) => [p.type, p.value]));
  const hh = (tmap.hour || '00').padStart(2, '0');
  const mm = (tmap.minute || '00').padStart(2, '0');
  const ss = (tmap.second || '00').padStart(2, '0');
  const timeStr = `${hh}:${mm}:${ss}`;

  return { dateStr, timeStr };
}

